import React, {useEffect, useMemo, useState} from "react";
import dayjs from "dayjs";
import "dayjs/locale/ru";

import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {Slider} from "@/components/ui/slider";
import {Button} from "@/components/ui/button";
import {Separator} from "@/components/ui/separator";
import {Badge} from "@/components/ui/badge";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    BarChart,
    Bar,
} from "recharts";

import {
    Calculator as CalcIcon,
    TrendingUp,
    Wallet,
    Percent,
    Calendar,
    DollarSign,
    PiggyBank,
    Target,
} from "lucide-react";

type TermValue = 3 | 5 | 1 | 2 | 4 | 6 | 7 | 8 | 9;

interface ScheduleRow {
    month: number;
    date: string;
    monthlyPayment: number;
    remaining: number;
}

function formatSom(value: number): string {
    const n = Number.isFinite(value) ? value : 0;
    return new Intl.NumberFormat("ru-RU", {
        maximumFractionDigits: 0,
    }).format(Math.round(n));
}

function formatSomFull(value: number): string {
    const n = Number.isFinite(value) ? value : 0;
    return `${new Intl.NumberFormat("ru-RU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n)} сом`;
}

const tooltipMoneyFormatter = (value: unknown) => {
    const n =
        typeof value === "number"
            ? value
            : typeof value === "string"
                ? Number(value)
                : 0;

    return [formatSomFull(Number.isFinite(n) ? n : 0), ""];
};

export default function CalculatorPhoneCredit() {
    const [phoneAmount, setPhoneAmount] = useState<number>(80000);
    const [downPayment, setDownPayment] = useState<number>(10000);
    const [term, setTerm] = useState<TermValue>(3);
    const [percent, setPercent] = useState<number>(30);
    const [releaseDay, setReleaseDay] = useState<string>(dayjs().format("YYYY-MM-DD"));
    const [costPrice, setCostPrice] = useState<number>(0);

    useEffect(() => {
        if (term === 3) setPercent(30);
        if (term === 5) setPercent(40);
    }, [term]);

    const calculations = useMemo(() => {
        const valid =
            phoneAmount > 0 &&
            downPayment >= 0 &&
            term > 0 &&
            percent >= 0 &&
            Boolean(releaseDay);

        if (!valid) {
            return {
                valid: false,
                total: 0,
                totalAfterPercent: 0,
                monthlyPayment: 0,
                closingDate: "",
                schedule: [] as ScheduleRow[],
                netProfit: 0,
            };
        }

        const remaining = Number(phoneAmount) - Number(downPayment);
        const totalAfterPercent = remaining * (1 + Number(percent) / 100);
        const total = totalAfterPercent + Number(downPayment);
        const monthlyPayment = totalAfterPercent / Number(term);

        const startDate = dayjs(releaseDay);
        const closeDate = startDate.add(Number(term), "month");
        const closingDate = closeDate.format("YYYY-MM-DD");

        const schedule: ScheduleRow[] = [];
        for (let i = 1; i <= Number(term); i++) {
            const paymentDate = startDate.add(i, "month");
            const paidAmount = monthlyPayment * i;
            const remainingAfterPayment = totalAfterPercent - paidAmount;

            schedule.push({
                month: i,
                date: paymentDate.format("YYYY-MM-DD"),
                monthlyPayment,
                remaining: Number(remainingAfterPayment.toFixed(2)),
            });
        }

        const netProfit = total - Number(costPrice || 0);

        return {
            valid: true,
            total,
            totalAfterPercent,
            monthlyPayment,
            closingDate,
            schedule,
            netProfit,
        };
    }, [phoneAmount, downPayment, term, percent, releaseDay, costPrice]);

    const balanceChartData = useMemo(() => {
        if (!calculations.valid) return [];
        return calculations.schedule.map((s) => ({
            month: `${s.month} мес`,
            payment: Math.round(s.monthlyPayment),
            balance: Math.max(0, Math.round(s.remaining)),
        }));
    }, [calculations]);

    const comparisonData = useMemo(() => {
        const terms: TermValue[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];
        return terms.map((t) => {
            const p = t === 3 ? 30 : t === 5 ? 40 : percent; // если хочешь фикс только для 3 и 5
            const remaining = Number(phoneAmount) - Number(downPayment);
            const totalAfterPercent = remaining * (1 + Number(p) / 100);
            const total = totalAfterPercent + Number(downPayment);
            const monthlyPayment = totalAfterPercent / Number(t);
            return {
                term: `${t} мес`,
                percent: p,
                monthlyPayment: Math.round(monthlyPayment),
                total: Math.round(total),
            };
        });
    }, [phoneAmount, downPayment, percent]);

    const downPaymentError =
        downPayment >= phoneAmount ? "Первоначальный взнос не может быть равен или больше суммы телефона" : "";
    const costPriceError =
        costPrice > phoneAmount ? "Себестоимость не может превышать стоимость телефона" : "";

    const canShowResults = calculations.valid && !downPaymentError && !costPriceError;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Калькулятор кредита</h1>
                </div>

                <div className="flex items-center gap-2">
                    <Badge variant="outline" className="gap-2">
                        <CalcIcon className="h-4 w-4"/>
                        Phone credit
                    </Badge>
                </div>
            </div>

            {/* Main grid */}
            <div className="grid gap-6 lg:grid-cols-3">
                {/* Inputs */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <CalcIcon className="h-5 w-5"/>
                            Параметры кредита
                        </CardTitle>
                        <CardDescription>Заполни данные, чтобы увидеть итоги и график</CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-8">
                        {/* Phone amount */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <Label className="text-base font-medium">Сумма телефона</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        value={phoneAmount}
                                        min={0}
                                        onChange={(e) => setPhoneAmount(Number(e.target.value))}
                                        className="w-36 text-right"
                                    />
                                    <span className="text-muted-foreground">сом</span>
                                </div>
                            </div>
                            <Slider
                                value={[phoneAmount]}
                                onValueChange={([v]) => setPhoneAmount(v)}
                                min={0}
                                max={500000}
                                step={1000}
                            />
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>0</span>
                                <span>{formatSom(500000)}</span>
                            </div>
                        </div>

                        {/* Down payment */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <Label className="text-base font-medium">Первоначальный взнос</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        value={downPayment}
                                        min={0}
                                        onChange={(e) => setDownPayment(Number(e.target.value))}
                                        className="w-36 text-right"
                                    />
                                    <span className="text-muted-foreground">сом</span>
                                </div>
                            </div>
                            <Slider
                                value={[Math.min(downPayment, phoneAmount || 0)]}
                                onValueChange={([v]) => setDownPayment(v)}
                                min={0}
                                max={Math.max(phoneAmount, 0)}
                                step={1000}
                            />
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>0</span>
                                <span>{formatSom(Math.max(phoneAmount, 0))}</span>
                            </div>
                            {downPaymentError ? (
                                <p className="text-sm text-destructive">{downPaymentError}</p>
                            ) : null}
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            {/* Term */}
                            <div className="space-y-2">
                                <Label className="text-base font-medium">Срок (месяцев)</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        value={term}
                                        min={1}
                                        max={9}
                                        onChange={(e) => setTerm(Number(e.target.value) as TermValue)}
                                        className="w-24 text-right"
                                    />
                                    <span className="text-muted-foreground">мес</span>
                                </div>
                                <Slider
                                    value={[Number(term)]}
                                    onValueChange={([v]) => setTerm(v as TermValue)}
                                    min={1}
                                    max={9}
                                    step={1}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Автопроцент: 3 мес = 30%, 5 мес = 40%
                                </p>
                            </div>

                            {/* Percent */}
                            <div className="space-y-2">
                                <Label className="text-base font-medium">Процент</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        value={percent}
                                        min={0}
                                        step={1}
                                        onChange={(e) => setPercent(Number(e.target.value))}
                                        className="w-24 text-right"
                                    />
                                    <span className="text-muted-foreground">%</span>
                                </div>
                                <Slider value={[percent]} onValueChange={([v]) => setPercent(v)} min={0} max={200}
                                        step={1}/>
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            {/* Release day */}
                            <div className="space-y-2">
                                <Label className="text-base font-medium">Дата кредитования</Label>
                                <Input type="date" value={releaseDay} onChange={(e) => setReleaseDay(e.target.value)}/>
                            </div>

                            {/* Cost price */}
                            <div className="space-y-2">
                                <Label className="text-base font-medium">Себестоимость</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        value={costPrice}
                                        min={0}
                                        onChange={(e) => setCostPrice(Number(e.target.value))}
                                        className="w-full"
                                    />
                                    <span className="text-muted-foreground">сом</span>
                                </div>
                                {costPriceError ? <p className="text-sm text-destructive">{costPriceError}</p> : null}
                            </div>
                        </div>

                        <Separator/>

                        <div className="flex items-center gap-2 flex-wrap">
                            <Button
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                    setPhoneAmount(80000);
                                    setDownPayment(10000);
                                    setTerm(3);
                                    setPercent(30);
                                    setReleaseDay(dayjs().format("YYYY-MM-DD"));
                                    setCostPrice(0);
                                }}
                            >
                                Сбросить
                            </Button>
                            <Badge variant="outline" className="gap-2">
                                <DollarSign className="h-4 w-4"/>
                                Остаток: {formatSom(Math.max(0, phoneAmount - downPayment))} сом
                            </Badge>
                        </div>
                    </CardContent>
                </Card>

                {/* Summary */}
                <div className="space-y-4">
                    <Card className="bg-primary text-primary-foreground">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <PiggyBank className="h-4 w-4"/>
                                Ежемесячный платеж
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-4xl font-bold">
                                {canShowResults ? formatSomFull(calculations.monthlyPayment) : "—"}
                            </p>
                            <p className="text-sm opacity-90 mt-1">
                                {canShowResults ? `Срок: ${term} мес` : "Заполни данные корректно"}
                            </p>
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-2 gap-4">
                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                    <Wallet className="h-4 w-4"/>
                                    <span className="text-xs">Общая сумма</span>
                                </div>
                                <p className="text-xl font-semibold">
                                    {canShowResults ? formatSomFull(calculations.total) : "—"}
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                    <TrendingUp className="h-4 w-4"/>
                                    <span className="text-xs">Наценка</span>
                                </div>
                                <p className="text-xl font-semibold">
                                    {canShowResults ? formatSomFull(calculations.total - phoneAmount) : "—"}
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                    <Percent className="h-4 w-4"/>
                                    <span className="text-xs">Процент</span>
                                </div>
                                <p className="text-xl font-semibold">{percent}%</p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                    <Calendar className="h-4 w-4"/>
                                    <span className="text-xs">Дата закрытия</span>
                                </div>
                                <p className="text-xl font-semibold">
                                    {canShowResults ? calculations.closingDate : "—"}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                <Target className="h-4 w-4"/>
                                <span className="text-xs">Чистая прибыль</span>
                            </div>
                            <p className="text-2xl font-semibold">
                                {canShowResults ? formatSomFull(calculations.netProfit) : "—"}
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Tabs: charts + schedule + compare */}
            <Tabs defaultValue="charts" className="space-y-4">
                <TabsList className="grid w-full max-w-md grid-cols-3">
                    <TabsTrigger value="charts">Графики</TabsTrigger>
                    <TabsTrigger value="schedule">График выплат</TabsTrigger>
                    <TabsTrigger value="compare">Сравнение</TabsTrigger>
                </TabsList>

                <TabsContent value="charts" className="space-y-4">
                    <div className="grid gap-6 lg:grid-cols-2">
                        <Card>
                            <CardHeader>
                                <CardTitle>Остаток долга</CardTitle>
                                <CardDescription>Как уменьшается остаток при равных платежах</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[300px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={balanceChartData}>
                                            <defs>
                                                <linearGradient id="balanceGradientPhone" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="hsl(var(--chart-1))"
                                                          stopOpacity={0.3}/>
                                                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-border"/>
                                            <XAxis
                                                dataKey="month"
                                                className="text-xs fill-muted-foreground"
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <YAxis
                                                className="text-xs fill-muted-foreground"
                                                tickLine={false}
                                                axisLine={false}
                                                tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "hsl(var(--popover))",
                                                    border: "1px solid hsl(var(--border))",
                                                    borderRadius: "var(--radius)",
                                                    color: "hsl(var(--popover-foreground))",
                                                }}
                                                formatter={tooltipMoneyFormatter}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="balance"
                                                name="Остаток"
                                                stroke="hsl(var(--chart-1))"
                                                strokeWidth={2}
                                                fill="url(#balanceGradientPhone)"
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Ежемесячный платеж</CardTitle>
                                <CardDescription>Размер платежа (одинаковый каждый месяц)</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[300px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={balanceChartData}>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-border"/>
                                            <XAxis
                                                dataKey="month"
                                                className="text-xs fill-muted-foreground"
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <YAxis
                                                className="text-xs fill-muted-foreground"
                                                tickLine={false}
                                                axisLine={false}
                                                tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "hsl(var(--popover))",
                                                    border: "1px solid hsl(var(--border))",
                                                    borderRadius: "var(--radius)",
                                                    color: "hsl(var(--popover-foreground))",
                                                }}
                                                formatter={tooltipMoneyFormatter}
                                            />
                                            <Legend/>
                                            <Bar dataKey="payment" name="Платеж" fill="hsl(var(--chart-1))"/>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="schedule">
                    <Card>
                        <CardHeader>
                            <CardTitle>График выплат</CardTitle>
                            <CardDescription>Помесячные платежи и остаток</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {!canShowResults ? (
                                <p className="text-sm text-muted-foreground">Нет данных для построения графика</p>
                            ) : (
                                <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                                    <Table>
                                        <TableHeader className="sticky top-0 bg-card">
                                            <TableRow>
                                                <TableHead className="w-[90px] text-center">Месяц</TableHead>
                                                <TableHead>Дата платежа</TableHead>
                                                <TableHead className="text-right">Платеж</TableHead>
                                                <TableHead className="text-right">Остаток</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {calculations.schedule.map((row) => (
                                                <TableRow key={row.month}>
                                                    <TableCell
                                                        className="text-center font-medium">{row.month}</TableCell>
                                                    <TableCell>{row.date}</TableCell>
                                                    <TableCell
                                                        className="text-right">{formatSomFull(row.monthlyPayment)}</TableCell>
                                                    <TableCell
                                                        className="text-right">{formatSomFull(Math.max(0, row.remaining))}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="compare">
                    <Card>
                        <CardHeader>
                            <CardTitle>Сравнение сроков</CardTitle>
                            <CardDescription>Как меняется платеж и итоговая сумма при разных сроках</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={comparisonData}>
                                        <CartesianGrid strokeDasharray="3 3" className="stroke-border"/>
                                        <XAxis dataKey="term" className="text-xs fill-muted-foreground" tickLine={false}
                                               axisLine={false}/>
                                        <YAxis
                                            className="text-xs fill-muted-foreground"
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: "hsl(var(--popover))",
                                                border: "1px solid hsl(var(--border))",
                                                borderRadius: "var(--radius)",
                                                color: "hsl(var(--popover-foreground))",
                                            }}
                                            formatter={tooltipMoneyFormatter}
                                        />
                                        <Legend/>
                                        <Bar dataKey="monthlyPayment" name="Ежемесячно" fill="hsl(var(--chart-1))"/>
                                        <Bar dataKey="total" name="Итог" fill="hsl(var(--chart-2))"/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Срок</TableHead>
                                            <TableHead className="text-right">Процент</TableHead>
                                            <TableHead className="text-right">Ежемесячно</TableHead>
                                            <TableHead className="text-right">Итог</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {comparisonData.map((row) => (
                                            <TableRow key={row.term}>
                                                <TableCell className="font-medium">{row.term}</TableCell>
                                                <TableCell className="text-right">{row.percent}%</TableCell>
                                                <TableCell
                                                    className="text-right">{formatSomFull(row.monthlyPayment)}</TableCell>
                                                <TableCell
                                                    className="text-right font-medium">{formatSomFull(row.total)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
